import React from 'react';
import { useMutation, gql } from '@apollo/client';
import { toast } from 'react-hot-toast';
import { SYNC_WORK_ORDERS } from '../../graphql/queries';

const SYNC_PROPERTIES = gql`
  mutation SyncStreamlineProperties {
    syncStreamlineProperties {
      successCount
      failureCount
      skippedCount
      timestamp
    }
  }
`;

const SYNC_AMENITIES = gql`
  mutation SyncStreamlineAmenities {
    syncStreamlineAmenities
  }
`;

const SYNC_RESERVATIONS = gql`
  mutation SyncStreamlineReservations($input: SyncReservationsInput!) {
    syncStreamlineReservations(input: $input) {
      successCount
      failureCount
      skippedCount
      timestamp
    }
  }
`;

const SYNC_REVIEWS = gql`
  mutation SyncStreamlineReviews($months: Int!) {
    syncStreamlineReviews(months: $months) {
      createdCount
      updatedCount
      skippedCount
      failureCount
      unmatchedPropertyCount
      windowsProcessed
      timestamp
    }
  }
`;

function DataSyncSection({ refetchJobs }) {
  // Extended timeout for sync operations (15 minutes for large syncs)
  const syncTimeout = 15 * 60 * 1000; // 15 minutes
  
  const [syncProperties, { loading: propertiesLoading, error: propertiesError, data: propertiesData }] = useMutation(SYNC_PROPERTIES, {
    context: { timeout: syncTimeout }
  });
  const [syncReservations, { loading: reservationsLoading, error: reservationsError, data: reservationsData }] = useMutation(SYNC_RESERVATIONS, {
    context: { timeout: syncTimeout }
  });
  const [syncWorkOrders, { loading: workOrdersLoading, error: workOrdersError, data: workOrdersData }] = useMutation(SYNC_WORK_ORDERS, {
    context: { timeout: syncTimeout }
  });
  const [syncAmenities, { loading: amenitiesLoading, error: amenitiesError, data: amenitiesData }] = useMutation(SYNC_AMENITIES, {
    context: { timeout: syncTimeout }
  });
  const [syncReviews, { loading: reviewsLoading, error: reviewsError, data: reviewsData }] = useMutation(SYNC_REVIEWS, {
    context: { timeout: syncTimeout }
  });

  const handleSyncProperties = async () => {
    try {
      await syncProperties();
      toast.success('Properties synchronized successfully');
    } catch (error) {
      toast.error('Failed to sync properties');
    }
  };

  const handleSyncReservations = async (totalDays = 42) => {
    try {
      // Get dates for configurable sync range
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 7); // 1 week back
      const endDate = new Date(now);
      endDate.setDate(now.getDate() + (totalDays - 7)); // Remaining days ahead

      await syncReservations({
        variables: {
          input: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        }
      });
      
      // Refetch jobs after sync to see the updates
      if (refetchJobs) await refetchJobs();
      toast.success('Reservations synchronized successfully');
    } catch (error) {
      toast.error('Failed to sync reservations');
    }
  };

  const handleSyncAmenities = async () => {
    try {
      await syncAmenities();
      toast.success('Amenities synchronized successfully');
    } catch (error) {
      toast.error('Failed to sync amenities');
    }
  };

  const handleSyncWorkOrders = async () => {
    try {
      const now = new Date();
      const startDate = new Date(now);
      startDate.setDate(now.getDate() - 7); // 7-day lookback to catch missed work orders

      const endDate = new Date(now);
      endDate.setMonth(now.getMonth() + 1);

      await syncWorkOrders({
        variables: {
          input: {
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
          }
        }
      });
      toast.success('Work orders synchronized successfully');
    } catch (error) {
      toast.error('Failed to sync work orders');
    }
  };

  const handleSyncReviews = async (months) => {
    try {
      await syncReviews({ variables: { months } });
      toast.success(months === 1 ? 'Recent reviews synchronized successfully' : '12-month review backfill completed');
    } catch (error) {
      toast.error('Failed to sync reviews');
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Data Synchronization</h2>
      
      {/* Properties Sync */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Properties Sync</h3>
            <p className="text-sm text-gray-500">Manually sync property data from Streamline</p>
          </div>
          <button
            onClick={handleSyncProperties}
            disabled={propertiesLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
          >
            {propertiesLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : 'Sync Now'}
          </button>
        </div>
        {propertiesData && (
          <div className="text-sm text-gray-600">
            Last sync: {new Date(propertiesData.syncStreamlineProperties.timestamp).toLocaleString()} 
            ({propertiesData.syncStreamlineProperties.successCount} succeeded, 
            {propertiesData.syncStreamlineProperties.failureCount} failed,
            {propertiesData.syncStreamlineProperties.skippedCount} skipped)
          </div>
        )}
        {propertiesError && (
          <div className="text-sm text-red-600 mt-2">
            Error: {propertiesError.message}
          </div>
        )}
      </div>

      {/* Reservations Sync */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Reservations Sync</h3>
            <p className="text-sm text-gray-500">Manually sync reservation data from Streamline. Use 2 weeks for fast syncs, 4 months for comprehensive coverage.</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handleSyncReservations(14)} // 2 weeks sync
              disabled={reservationsLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
            >
              {reservationsLoading ? 'Syncing...' : '2 Weeks'}
            </button>
            <button
              onClick={() => handleSyncReservations(127)} // 4 months sync (1 week back + 4 months ahead = ~127 days) 
              disabled={reservationsLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
            >
              {reservationsLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Syncing...
                </>
              ) : '4 Months'}
            </button>
          </div>
        </div>
        {reservationsData && (
          <div className="text-sm text-gray-600">
            Last sync: {new Date(reservationsData.syncStreamlineReservations.timestamp).toLocaleString()} 
            ({reservationsData.syncStreamlineReservations.successCount} succeeded, 
            {reservationsData.syncStreamlineReservations.failureCount} failed,
            {reservationsData.syncStreamlineReservations.skippedCount} skipped)
          </div>
        )}
        {reservationsError && (
          <div className="text-sm text-red-600 mt-2">
            Error: {reservationsError.message}
            {reservationsError.message.includes('524') && (
              <div className="text-xs text-gray-600 mt-1">
                Gateway timeout - try using the "2 Weeks" option for smaller syncs that complete faster.
              </div>
            )}
          </div>
        )}
        <div className="text-xs text-gray-500 mt-2">
          <strong>Tip:</strong> If you get timeout errors (524), use "2 Weeks" option. For historical data, run multiple 2-week syncs as needed.
        </div>
        
        {/* Auto-Sync Status Info */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-2">🔄 Auto-Sync Information</h4>
          <div className="text-xs text-blue-700 space-y-1">
            <div><strong>Auto-sync window:</strong> 4 months (1 week back + 4 months ahead)</div>
            <div><strong>Auto-sync interval:</strong> Every 4 hours (configurable in Job Settings)</div>
            <div><strong>Monitor auto-sync:</strong> Check backend logs for "AUTO-SYNC" messages</div>
            <div><strong>Note:</strong> Auto-sync now uses optimized batch operations for better performance</div>
          </div>
        </div>
      </div>

      {/* Work Orders Sync */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Work Orders Sync</h3>
            <p className="text-sm text-gray-500">Manually sync work order data from Streamline (current month + 1 month ahead)</p>
          </div>
          <button
            onClick={handleSyncWorkOrders}
            disabled={workOrdersLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
          >
            {workOrdersLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : 'Sync Now'}
          </button>
        </div>
        {workOrdersData && (
          <div className="text-sm text-gray-600">
            Last sync: {new Date(workOrdersData.syncStreamlineWorkOrders.timestamp).toLocaleString()} 
            ({workOrdersData.syncStreamlineWorkOrders.successCount} succeeded, 
            {workOrdersData.syncStreamlineWorkOrders.failureCount} failed,
            {workOrdersData.syncStreamlineWorkOrders.skippedCount} skipped)
          </div>
        )}
        {workOrdersError && (
          <div className="text-sm text-red-600 mt-2">
            Error: {workOrdersError.message}
          </div>
        )}
      </div>

      {/* Amenities Sync */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Reviews Sync</h3>
            <p className="text-sm text-gray-500">Import guest reviews from Streamline in API-safe 31-day windows.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleSyncReviews(1)}
              disabled={reviewsLoading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {reviewsLoading ? 'Syncing...' : 'Sync Recent Reviews'}
            </button>
            <button
              onClick={() => handleSyncReviews(12)}
              disabled={reviewsLoading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
            >
              {reviewsLoading ? 'Syncing...' : 'Backfill 12 Months'}
            </button>
          </div>
        </div>
        {reviewsData?.syncStreamlineReviews && (
          <div className="mt-3 rounded-md bg-sky-50 p-3 text-sm text-sky-900">
            <div>Last sync: {new Date(reviewsData.syncStreamlineReviews.timestamp).toLocaleString()}</div>
            <div className="mt-1 text-xs text-sky-800">
              {reviewsData.syncStreamlineReviews.createdCount} created,{' '}
              {reviewsData.syncStreamlineReviews.updatedCount} updated,{' '}
              {reviewsData.syncStreamlineReviews.skippedCount} skipped,{' '}
              {reviewsData.syncStreamlineReviews.unmatchedPropertyCount} unmatched properties,{' '}
              {reviewsData.syncStreamlineReviews.failureCount} failed windows across{' '}
              {reviewsData.syncStreamlineReviews.windowsProcessed} completed windows
            </div>
          </div>
        )}
        {reviewsError && <div className="text-sm text-red-600 mt-2">Error: {reviewsError.message}</div>}
        <p className="text-xs text-gray-500 mt-2">
          The recent sync covers 31 days. The historical backfill splits the last 12 months into consecutive requests because Streamline limits each request to 31 days.
        </p>
      </div>

      {/* Amenities Sync */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Amenities Sync</h3>
            <p className="text-sm text-gray-500">Sync the amenities master list and per-property amenities from Streamline</p>
          </div>
          <button
            onClick={handleSyncAmenities}
            disabled={amenitiesLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
          >
            {amenitiesLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Syncing...
              </>
            ) : 'Sync Now'}
          </button>
        </div>
        {amenitiesData && (() => {
          try {
            const result = JSON.parse(amenitiesData.syncStreamlineAmenities);
            if (result.error) return (
              <div className="text-sm text-red-600">Error: {result.error}</div>
            );
            return (
              <div className="text-sm text-gray-600">
                Last sync: {new Date(result.timestamp).toLocaleString()} — 
                Master list: {result.masterList.success} synced, {result.masterList.failed} failed | 
                Properties: {result.properties.success} synced, {result.properties.failed} failed
              </div>
            );
          } catch {
            return <div className="text-sm text-gray-600">{amenitiesData.syncStreamlineAmenities}</div>;
          }
        })()}
        {amenitiesError && (
          <div className="text-sm text-red-600 mt-2">
            Error: {amenitiesError.message}
          </div>
        )}
        <div className="text-xs text-gray-500 mt-2">
          <strong>Note:</strong> This syncs the full amenity catalog first, then fetches amenities for each active property. May take a few minutes for large portfolios.
        </div>
      </div>
    </div>
  );
}

export default DataSyncSection;
